<template>
  <div
    :class="{
      'tw-p-6': !inModal,
      'two-drawer-layout__screen-height--after-secondary-subheader':
        !usingNewLayout && !inModal,
      'tw-h-full': inModal,
    }"
  >
    <div
      class="tw-h-full tw-flex tw-flex-row tw-w-full"
      :class="[layoutClasses, borderClasses]"
    >
      <InboxEmpty v-if="empty && !anyFilterPresent && !loading" />
      <template v-else>
        <div
          class="
            tw-h-full
            tw-shrink-0
            tw-grow
            tw-rounded-l-lg
            tw-p-4
            tw-w-condensed-sidepane
            tw-max-w-condensed-sidepane
            tw-overflow-y-auto
            tw-bg-sf-snow-lighter
          "
        >
          <div v-if="loading">
            <SfSkeletonLoader
              v-for="i in 15"
              :key="i"
              height="2rem"
              :width="['10rem', '15rem', '20rem'][i % 3]"
              class="tw-px-2"
            />
          </div>
          <InboxLeftSidepane
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
            @select="setSelected"
          >
            <template v-if="anyFilterPresent" #before>
              <SfListItem
                item-id="company-inbox-filter-results"
                background="transparent"
                font-weight="normal"
                cornered
                :selected="selectedCategory === FILTERED_CATEGORY"
                @item-click="setSelected(FILTERED_CATEGORY, '')"
              >
                <template #icon>
                  <span :class="filterCategoryIconClass">
                    <i class="fa fa-list-ul" />
                  </span>
                </template>
                <template #default>
                  <span class="tw-font-medium">
                    {{ I18n.t('inbox.company.filter_results.title') }}
                  </span>
                </template>
              </SfListItem>
              <div
                v-if="!empty"
                class="tw-flex tw-flex-col tw-justify-center tw-h-8"
              >
                <div class="tw-h-px tw-bg-sf-neutral-light-1" />
              </div>
            </template>
          </InboxLeftSidepane>
        </div>
        <div
          data-testid="company-inbox-communications-container"
          class="
            tw-h-full
            tw-shrink
            tw-grow
            tw-overflow-y-auto
            tw-rounded-r-lg
            tw-bg-sf-snow-lighter
          "
        >
          <div v-if="loading">
            <RemarkSkeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
          </div>
          <InboxCommunicationContainer
            v-else
            :selected-category="selectedCategory"
            :selected-item="selectedItem"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, watch, watchEffect } from 'vue'
import { useStore } from 'vuex'

import InboxCommunicationContainer from '@app/application/communication/screens/inbox/components/inbox-communication-container.vue'
import InboxEmpty from '@app/application/communication/screens/inbox/components/inbox-empty.vue'
import InboxLeftSidepane from '@app/application/communication/screens/inbox/components/inbox-left-sidepane.vue'
import RemarkSkeleton from '@app/shared/vue/components/molecules/remark/remark-skeleton.vue'
import SfSkeletonLoader from '@app/shared/vue/components/sf-skeleton-loader.vue'
import { isUsingNewLayout } from '@app/adapters/silverfin'
import {
  ALL_CATEGORY,
  FILTERED_CATEGORY,
  UNREAD_CATEGORY,
} from '@app/application/communication/screens/inbox/store/utils'
import { useInboxFiltersStore } from '@app/application/communication/screens/inbox/store/inbox-filters'
import { storeToRefs } from 'pinia'
import { SfListItem } from '@silverfin/ui-components'
import useGroupedFilteredCommunications from '@app/application/communication/screens/inbox/composables/use-grouped-filtered-communications'
import useInboxUrlStorage from '@app/application/communication/screens/inbox/composables/use-inbox-url-storage'

const props = defineProps({
  initialCategory: {
    type: String,
    default: UNREAD_CATEGORY,
  },
  initialItem: {
    type: String,
    default: '',
  },
  inModal: {
    type: Boolean,
    default: false,
  },
})

const store = useStore()
const inboxFiltersStore = useInboxFiltersStore()

const selectedCategory = useInboxUrlStorage(
  'category',
  props.initialCategory,
  (val) => val
)
const selectedItem = useInboxUrlStorage('item', props.initialItem, (val) => val)

const filterCategoryIconClass = computed(() =>
  selectedCategory.value === FILTERED_CATEGORY
    ? 'tw-text-sf-gray-darker'
    : 'tw-text-sf-gray-light'
)

const { anyFilterPresent, receivedInitialValues } =
  storeToRefs(inboxFiltersStore)
const { groupedCommunication, filteredUnreadNotificationCount } =
  useGroupedFilteredCommunications()

// Note that this watcher explicitly doesn't watch `selectedCategory`. We only want to run it when anyFilterPresent
// changes, and then, if there are no filters applied anymore and we are on the filtered category, switch away from it.
watch(anyFilterPresent, () => {
  if (receivedInitialValues.value) {
    if (anyFilterPresent.value) {
      setSelected(FILTERED_CATEGORY, '')
    } else if (selectedCategory.value === FILTERED_CATEGORY) {
      setSelected(ALL_CATEGORY, '')
    }
  }
})

const empty = computed(
  () => Object.keys(groupedCommunication.value).length === 0
)

const emptyNotifications = computed(() => {
  return Object.keys(store.state.notifications.notifications).length === 0
})

const loading = computed(() => {
  return (
    (emptyNotifications.value && store.getters.loadingNotifications) ||
    store.getters.loadingWorkflows ||
    store.getters.loadingMentionUsernames ||
    (empty.value && store.getters.fetchingCommunicationsInProgress)
  )
})

const layoutClasses = computed(() => {
  if (props.inModal) return []
  return ['tw-rounded-lg', 'tw-max-w-condensed-content', 'tw-mx-auto']
})

const borderClasses = computed(() => {
  if (props.inModal) return []
  return empty.value && !loading.value && !anyFilterPresent.value
    ? []
    : ['tw-border', 'tw-border-solid', 'tw-border-sf-neutral-lighter-2']
})

function setSelected(category, item) {
  store.dispatch('resetNotificationReadCache')

  selectedCategory.value = category
  selectedItem.value = item
}

async function refreshCommunicationData() {
  await store.dispatch('fetchCommunication')
}

store.dispatch('fetchWorkflows')
refreshCommunicationData()
store.dispatch('fetchNotifications')
store.dispatch('fetchMentionUsernames')

const usingNewLayout = isUsingNewLayout()

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
})

watchEffect(() => {
  if (
    loading.value ||
    selectedCategory.value === ALL_CATEGORY ||
    selectedCategory.value === FILTERED_CATEGORY
  ) {
    return
  }

  if (selectedCategory.value === UNREAD_CATEGORY) {
    if (filteredUnreadNotificationCount.value === 0) {
      setSelected(ALL_CATEGORY, '')
    }
    return
  }

  if (
    !groupedCommunication.value[selectedCategory.value]?.[selectedItem.value]
  ) {
    setSelected(ALL_CATEGORY, '')
  }
})
</script>
